import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { LogEvent } from "../models/LogEvents";
import { grey } from "@material-ui/core/colors";
import { getTwilioPlatformEventPage } from "../services/api/platformEvents.api";
import { Fetcher } from "../components/Fetcher";
import { LogListContainer } from "../components/LogListContainer";
import { Pagination } from "../components/Pagination";
import { FilterContainer } from "../components/FilterContainer";
import { DEFAULT_PAGING_SIZE } from "../constants/index";
import { MessageCard } from "../components/MessageCard";
import Typography from "@material-ui/core/Typography";
import { generateTableHeight } from "../utils";
import {
	getPlatformLogsFiltersLocalStorageValue,
	resetPlatformLogsFiltersLocalStorageValue,
	setPlatformLogsFiltersLocalStorageValue,
} from "../globalState/LocalStorage/TwilioLocalStorageAccessors";

interface PlatformEventsPageProps {
	productId: string;
}

const useStyles = makeStyles((theme) => ({
	title: {
		marginBottom: theme.spacing(3),
	},
	tableWrapper: {
		maxWidth: 1200,
		overflow: "hidden",
		borderBottomColor: grey[100],
		borderBottomWidth: 1,
		borderBottomStyle: "solid",
	},
	tableTitle: {
		zIndex: 1,
	},
}));

/**
 * Used to render the piece of the page that refreshes upon fetcher information being updated.
 * @param events
 * @param classes
 * @param setPageNumber
 * @param setRowsPerPage
 * @param pageNumber
 * @param rowsPerPage
 */
const renderList = (
	events: LogEvent[],
	classes: Record<any, string>,
	setPageNumber: (n: number) => void,
	setRowsPerPage: (n: number) => void,
	pageNumber: number,
	rowsPerPage: number,
) => {
	return (
		<div>
			{events.length > 0 ? (
				<div>
					<div style={{ height: generateTableHeight(events.length) }} className={classes.tableWrapper}>
						<LogListContainer logs={events}></LogListContainer>
					</div>
					<Pagination
						pageNumber={pageNumber}
						rowsPerPage={rowsPerPage}
						setPageFunction={setPageNumber}
						setRowsPerPageFunction={setRowsPerPage}
						currentPageLength={events.length}
					></Pagination>
				</div>
			) : (
				<MessageCard message="No platform events found." />
			)}
		</div>
	);
};

/**
 * This page displays a list of platform events using DateTimeFilters
 * as well as a LogCardContainer and Pagination components.
 * It uses the fetcher combined with the PlatformEvents api
 * to refresh the list from cle on date change or page change.
 */
export const PlatformEventsPage = (props: PlatformEventsPageProps) => {
	const classes = useStyles();
	const [pageNumber, setPageNumber] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_PAGING_SIZE);
	const [update, forceUpdate] = React.useState(false);
	const filterObject = getPlatformLogsFiltersLocalStorageValue();

	function handleDateChange(filters) {
		setPlatformLogsFiltersLocalStorageValue(filters);
		setPageNumber(0);
		forceUpdate(!update);
	}

	function handleReset() {
		forceUpdate(!update);
		setPageNumber(0);
		return resetPlatformLogsFiltersLocalStorageValue();
	}

	const platformEventPageRequest = {
		startDate: filterObject.startDate,
		endDate: filterObject.endDate,
		page: pageNumber,
		pageSize: rowsPerPage,
	};

	return (
		<div>
			<Typography className={classes.title} variant="h4" data-testid="platform-events-page">
				Platform Events
			</Typography>
			<FilterContainer
				dateFilter={{
					startDate: filterObject.startDate,
					endDate: filterObject.endDate,
					refreshDateTimespan: filterObject.refreshDateTimespan,
				}}
				onUpdate={handleDateChange}
				onReset={handleReset}
			></FilterContainer>
			<Fetcher action={getTwilioPlatformEventPage(props.productId, platformEventPageRequest)}>
				{(response) => renderList(response.data, classes, setPageNumber, setRowsPerPage, pageNumber, rowsPerPage)}
			</Fetcher>
		</div>
	);
};
